import { defineStore } from "pinia";
import { ref } from "vue";
import { useAxios } from "@/composables/api.js";
import { setToken, removeToken } from "@/composables/token.js";
import { useAssessmentStore } from "@/stores/assessment.js";
import { catchError } from "@/composables/error.js";

export const useCandidateStore = defineStore("candidate", () => {
  const assessmentStore = useAssessmentStore();

  const invitation = ref({});
  const candidate = ref({});
  const auth_token_game = ref(false);
  const joinInformations = ref({
    email: null,
    first_name: null,
    last_name: null,
    password: null,
    phone_number: {
      formatted: null,
    },
  });

  if (localStorage.getItem("auth_token_game")) {
    setToken(
      localStorage.getItem("auth_token_game"),
      "auth_token_game",
      auth_token_game
    );
  }

  async function getCandidate(fallbackRoute) {
    if (Date.now() >= auth_token_game.value.exp * 1000) {
      removeToken("auth_token_game", auth_token_game);
      this.$router.push({ name: fallbackRoute });
      return;
    }

    try {
      const { data } = await useAxios.get("/candidates-sessions");

      Object.assign(candidate.value, data.candidate);

      return data;
    } catch (error) {
      removeToken("auth_token_game", auth_token_game);
      this.$router.push({ name: fallbackRoute });
      return catchError(error);
    }
  }

  async function login(email, password) {
    try {
      const { data } = await useAxios.post("/candidates-sessions", {
        email,
        password,
      });

      const tokenPromise = new Promise((resolve) => {
        setToken(data.token, "auth_token_game", auth_token_game);
        resolve();
      });

      return tokenPromise.then(async () => {
        Object.assign(candidate.value, data.candidate);

        return data;
      });
    } catch (error) {
      return catchError(error);
    }
  }

  async function getInvitation(invitation_id) {
    try {
      const { data } = await useAxios.get(`/invitations/${invitation_id}`);

      Object.assign(invitation.value, data);

      Object.assign(joinInformations.value, {
        ...joinInformations.value,
        email: data.email,
        invitation_id: invitation_id,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function joinNewCampaignFromInvitation(invitation_id) {
    try {
      const { data } = await useAxios.put(`/invitations/${invitation_id}`);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function createCandidate() {
    try {
      const candidateObj = {
        ...joinInformations.value,
        phone_number: joinInformations.value.phone_number.number,
      };

      const { data } = await useAxios.post("/candidates", candidateObj);

      setToken(data.token, "auth_token_game", auth_token_game);

      Object.assign(candidate.value, data.candidate);

      setTimeout(() => {
        joinInformations.value = {
          email: null,
          first_name: null,
          last_name: null,
          password: null,
          phone_number: {
            formatted: null,
          },
        };
      }, 600);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getEmailVerificationToken(email) {
    try {
      const { data } = await useAxios.post("/emails-verifications", {
        email,
      });

      return data.verification_id;
    } catch (error) {
      return catchError(error);
    }
  }

  async function emailVerification(email, token, code) {
    try {
      const { data } = await useAxios.put(`/emails-verifications/${token}`, {
        email,
        code,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function getPhoneVerificationToken(phone_number) {
    try {
      const { data } = await useAxios.post("/phones-verifications", {
        phone_number,
      });

      return data.verification_id;
    } catch (error) {
      return catchError(error);
    }
  }

  async function phoneVerification(phone_number, token, code) {
    try {
      const { data } = await useAxios.put(`/phones-verifications/${token}`, {
        phone_number,
        code,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updatePhone(phone_number, verification_id) {
    try {
      const { data } = await useAxios.put("/candidates/me/phone-number", {
        phone_number,
        verification_id,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updatePassword(previous_password, new_password) {
    try {
      const { data } = await useAxios.put("/candidates/me/password", {
        previous_password,
        new_password,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function forgotPassword(email) {
    try {
      const { data } = await useAxios.post("/candidates-passwords-resets", {
        email,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function resetPassword(reset_id, password) {
    try {
      const { data } = await useAxios.put(
        `/candidates-passwords-resets/${reset_id}`,
        {
          password,
        }
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function resetEmail(email, phone_number, verification_id) {
    try {
      const { data } = await useAxios.put("/candidates-emails-resets", {
        email,
        phone_number,
        verification_id,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  function clear() {
    removeToken("auth_token_game", auth_token_game);
    candidate.value = {};
  }

  function logout() {
    const clearFuncs = [assessmentStore.clear, clear];

    clearFuncs.forEach((fn) => fn());

    this.$router.push({ name: "game-login" });
  }

  return {
    invitation,
    candidate,
    auth_token_game,
    joinInformations,
    getInvitation,
    joinNewCampaignFromInvitation,
    createCandidate,
    getCandidate,
    login,
    logout,
    getEmailVerificationToken,
    emailVerification,
    getPhoneVerificationToken,
    phoneVerification,
    updatePhone,
    updatePassword,
    forgotPassword,
    resetPassword,
    resetEmail,
  };
});
